import React, { useMemo, useState } from "react";
import { toLocalDisplay } from "../../utils/datetimeLocal";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, Search, X, Users, CheckCircle2, Ban,
  Clock, History, Loader2, Pencil, BookOpen,
  ArrowUp, ArrowDown, ArrowUpDown,
} from "lucide-react";

import {
  useAdminGetDietQuery,
  useAdminReleaseDietResultsMutation,
} from "../../api/examsApi";
import {
  useExaminerGetDietApplicationsQuery,
  useExaminerGradeDietApplicationMutation,
} from "../../api/examinerApi";
import { useAccess } from "../../../../utils/useAccess";
import { PERMS } from "../../../../utils/perms";

/* The board PUBLISHES (closes grading); the secretariat RELEASES to candidates
   from the admin diet screen. This page only offers the first of the two. */
const PUBLISH_ROLES = ["committee_examination_board.chairman"];

/* ─── helpers ─────────────────────────────────────────────────────────── */

const STATUS_COLORS: Record<string, string> = {
  draft:    "bg-gray-50 text-gray-400 border-gray-100",
  open:     "bg-emerald-50 text-emerald-700 border-emerald-100",
  closed:   "bg-rose-50 text-rose-700 border-rose-100",
  archived: "bg-amber-50 text-amber-700 border-amber-100",
};

const APP_STATUS_COLORS: Record<string, string> = {
  draft:     "bg-gray-50 text-gray-500 border-gray-100",
  submitted: "bg-blue-50 text-blue-700 border-blue-100",
  paid:      "bg-cyan-50 text-cyan-700 border-cyan-100",
  approved:  "bg-emerald-50 text-emerald-700 border-emerald-100",
  rejected:  "bg-rose-50 text-rose-700 border-rose-100",
  completed: "bg-indigo-50 text-indigo-700 border-indigo-100",
};

const RESULT_STATUS_COLORS: Record<string, string> = {
  pending:         "bg-gray-50 text-gray-400 border-gray-100",
  passed:          "bg-emerald-50 text-emerald-700 border-emerald-100",
  failed:          "bg-rose-50 text-rose-700 border-rose-100",
  recommended:     "bg-teal-50 text-teal-700 border-teal-100",
  not_recommended: "bg-orange-50 text-orange-700 border-orange-100",
  partial_pass:    "bg-amber-50 text-amber-700 border-amber-100",
  referred:        "bg-amber-50 text-amber-700 border-amber-100",
  full_reset:      "bg-red-50 text-red-700 border-red-100",
  absent:          "bg-gray-100 text-gray-600 border-gray-200",
};

function fmt(dt?: string | null) {
  return toLocalDisplay(dt);
}

function safeMsg(err: any) {
  return err?.data?.message || err?.data?.error || err?.error || err?.message || "Something went wrong";
}

type SortDir = "asc" | "desc";
type SortCol = "name" | "status" | "result" | "submitted_at" | "center";

function SortIcon({ col, active, dir }: { col: SortCol; active: SortCol; dir: SortDir }) {
  if (col !== active) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
  return dir === "asc"
    ? <ArrowUp className="w-3 h-3 ml-1 text-indigo-500" />
    : <ArrowDown className="w-3 h-3 ml-1 text-indigo-500" />;
}

/* ─── Grade Modal ─────────────────────────────────────────────────────── */

function GradeModal({
  app,
  dietId,
  onClose,
  onDone,
}: {
  app: any;
  dietId: string;
  onClose: () => void;
  onDone: (updated: any) => void;
}) {
  const isPaperBased = (app.papers ?? []).length > 0;
  const [gradeApp, { isLoading: grading }] = useExaminerGradeDietApplicationMutation();

  const defaultStatus = app.result?.overall_status ?? "recommended";
  const [simpleStatus, setSimpleStatus] = useState<string>(defaultStatus);
  const [simpleRemark, setSimpleRemark] = useState<string>(
    app.result?.remarks ?? (defaultStatus === "recommended" ? "P" : "F")
  );

  const handleOutcomeChange = (s: string) => {
    setSimpleStatus(s);
    setSimpleRemark(s === "recommended" ? "P" : s === "absent" ? "A" : "F");
  };

  const [paperGrades, setPaperGrades] = useState<Record<string, { status: string; score: string }>>(() => {
    const init: Record<string, { status: string; score: string }> = {};
    for (const p of app.papers ?? []) {
      const pid = p.exam_paper_id ?? p.paper?.id;
      if (!pid) continue;
      init[pid] = {
        status: p.status === "pending" ? "pass" : p.status,
        score:  p.score != null ? String(p.score) : "",
      };
    }
    return init;
  });

  const [toast, setToast] = useState<string | null>(null);

  const onSubmit = async () => {
    try {
      let body: any;
      if (isPaperBased) {
        body = {
          papers: (app.papers ?? []).map((p: any) => {
            const pid = p.exam_paper_id ?? p.paper?.id;
            const g   = paperGrades[pid] ?? { status: "pass", score: "" };
            return {
              exam_paper_id: pid,
              status:        g.status,
              score:         g.score !== "" ? Number(g.score) : null,
            };
          }),
        };
      } else {
        body = { overall_status: simpleStatus, remarks: simpleRemark };
      }
      const result = await gradeApp({ dietId, appId: app.id, body }).unwrap();
      onDone(result);
    } catch (err) {
      setToast(safeMsg(err));
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">

        {/* header */}
        <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/40 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gray-900 rounded-2xl text-white">
              <Pencil className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-black text-gray-900 text-xs uppercase tracking-widest">Grade Application</h3>
              <p className="text-[10px] text-gray-400 font-bold mt-0.5 uppercase tracking-widest">
                {app.user?.name} — {app.exam?.code}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {toast && (
            <div className="p-3 rounded-2xl bg-rose-50 border border-rose-100 text-rose-800 text-[10px] font-black uppercase tracking-widest flex justify-between items-center">
              {toast}
              <button onClick={() => setToast(null)}><X className="w-4 h-4 opacity-50" /></button>
            </div>
          )}

          {isPaperBased ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-4 h-4 text-indigo-500" />
                <span className="text-[10px] font-black uppercase tracking-widest text-gray-700">Paper Results</span>
              </div>
              {(app.papers ?? []).map((p: any) => {
                const pid = p.exam_paper_id ?? p.paper?.id;
                const g   = paperGrades[pid] ?? { status: "pass", score: "" };
                return (
                  <div key={pid} className="bg-gray-50 rounded-2xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">{p.paper?.code}</span>
                        <span className="ml-2 text-xs font-bold text-gray-700">{p.paper?.title}</span>
                      </div>
                      {p.paper?.pass_mark && (
                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Pass mark: {p.paper.pass_mark}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        {["pass", "fail", "absent"].map((s) => (
                          <button
                            key={s}
                            onClick={() => setPaperGrades((prev) => ({ ...prev, [pid]: { ...g, status: s } }))}
                            className={`px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${
                              g.status === s
                                ? s === "pass" ? "bg-emerald-600 text-white" : s === "fail" ? "bg-rose-600 text-white" : "bg-gray-600 text-white"
                                : "bg-white border border-gray-200 text-gray-500 hover:border-gray-400"
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-1.5 ml-auto">
                        <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Score</label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={g.score}
                          onChange={(e) => setPaperGrades((prev) => ({ ...prev, [pid]: { ...g, score: e.target.value } }))}
                          placeholder="—"
                          className="w-16 border-b-2 border-gray-200 py-1 text-center text-sm font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">Outcome</label>
                <div className="flex gap-3">
                  {[
                    { value: "recommended",     label: "P — Recommended" },
                    { value: "not_recommended", label: "F — Not Recommended" },
                    { value: "absent",          label: "A — Absent" },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => handleOutcomeChange(value)}
                      className={`flex-1 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest transition-all border-2 ${
                        simpleStatus === value
                          ? value === "recommended"
                            ? "bg-teal-600 text-white border-teal-600"
                            : value === "not_recommended"
                            ? "bg-orange-600 text-white border-orange-600"
                            : "bg-gray-600 text-white border-gray-600"
                          : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {simpleStatus !== "absent" && (
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">Grade</label>
                  <div className="flex gap-3">
                    {[
                      { value: "P", label: "P", color: "teal" },
                      { value: "F", label: "F", color: "rose" },
                    ].map(({ value, label, color }) => (
                      <button
                        key={value}
                        onClick={() => setSimpleRemark(value)}
                        className={`flex-1 py-3 rounded-2xl text-lg font-black transition-all border-2 ${
                          simpleRemark === value
                            ? `bg-${color}-600 text-white border-${color}-600`
                            : "bg-white text-gray-500 border-gray-200 hover:border-gray-600"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-6 bg-gray-50 border-t border-gray-100 flex gap-3 shrink-0">
          <button
            onClick={onClose}
            className="flex-1 py-3 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={grading}
            className="flex-[2] bg-gray-900 text-white rounded-2xl py-3 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all disabled:opacity-30 flex items-center justify-center gap-2"
          >
            {grading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : "Submit Grade"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main page ───────────────────────────────────────────────────────── */

export default function ExaminerDietGradingPage() {
  const { id: examId = "", dietId = "" } = useParams();

  const { can } = useAccess();
  // Role OR capability: the Chief Examiner keeps it by office, and anyone
  // deliberately granted exam.result.publish gets it too. The API checks the
  // permission, so the role alone would show a button that then fails.
  const canPublish =
    can.anyRole(PUBLISH_ROLES) || can.perm(PERMS.exam.resultPublish);

  const { data: diet, isLoading: dietLoading, refetch: refetchDiet } =
    useAdminGetDietQuery(dietId, { skip: !dietId });

  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch]             = useState("");
  const [sort, setSort]                 = useState<{ col: SortCol; dir: SortDir }>({ col: "submitted_at", dir: "desc" });
  const [gradeTarget, setGradeTarget]   = useState<any | null>(null);

  const {
    data: rawApplications = [],
    isLoading: appsLoading,
    isFetching: appsFetching,
    refetch: refetchApps,
  } = useExaminerGetDietApplicationsQuery(
    { dietId, ...(statusFilter ? { status: statusFilter } : {}) },
    { skip: !dietId }
  );

  const [publishResults, { isLoading: publishingAll }] = useAdminReleaseDietResultsMutation();

  const [localApps, setLocalApps] = useState<any[]>([]);
  const applications = localApps.length > 0 ? localApps : (rawApplications as any[]);

  React.useEffect(() => {
    if ((rawApplications as any[]).length > 0) setLocalApps(rawApplications as any[]);
  }, [rawApplications]);

  const searched = useMemo(() => {
    if (!search.trim()) return applications;
    const t = search.toLowerCase();
    return applications.filter((a: any) =>
      (a.user?.name ?? "").toLowerCase().includes(t) ||
      (a.user?.email ?? "").toLowerCase().includes(t) ||
      (a.exam?.code ?? "").toLowerCase().includes(t)
    );
  }, [applications, search]);

  const filtered = useMemo(() => {
    const list = [...searched];
    list.sort((a, b) => {
      let av: any, bv: any;
      switch (sort.col) {
        case "name":         av = a.user?.name ?? "";             bv = b.user?.name ?? "";             break;
        case "status":       av = a.status ?? "";                 bv = b.status ?? "";                 break;
        case "result":       av = a.result?.overall_status ?? ""; bv = b.result?.overall_status ?? ""; break;
        case "center":       av = a.center?.name ?? "";           bv = b.center?.name ?? "";           break;
        case "submitted_at": av = a.submitted_at ?? "";           bv = b.submitted_at ?? "";           break;
        default: av = ""; bv = "";
      }
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ?  1 : -1;
      return 0;
    });
    return list;
  }, [searched, sort]);

  const toggleSort = (col: SortCol) =>
    setSort((prev) =>
      prev.col === col ? { col, dir: prev.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" }
    );

  const handleGradeDone = (updated: any) => {
    if (updated) setLocalApps((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    setGradeTarget(null);
    refetchApps();
  };

  // Publishing is a whole-diet act — one candidate at a time is not something
  // the board does, and releasing to candidates belongs to the secretariat.
  const doPublishAll = async () => {
    if (
      !window.confirm(
        "Publish every result in this diet?\n\n" +
          "Candidates with no marks at all will be recorded ABSENT, and published results are final. " +
          "Candidates do not see anything until the secretariat releases the diet.",
      )
    )
      return;
    try {
      const res: any = await publishResults({ dietId: dietId! }).unwrap();
      setLocalApps([]);
      refetchApps();
      window.alert(res?.message || "Results published.");
    } catch (e: any) {
      window.alert(e?.data?.message || "Could not publish results.");
    }
  };

  const gradedCount    = applications.filter((a: any) => !!a.result).length;
  const pendingCount   = applications.length - gradedCount;
  const publishedCount = applications.filter(
    (a: any) => !!(a.result?.finalized_at || a.result?.published_at),
  ).length;
  const hasUnpublished = applications.some(
    (a: any) => a.result && !a.result.finalized_at && !a.result.published_at,
  );
  const isBusy         = dietLoading || appsLoading || appsFetching;

  const Th = ({ col, label }: { col: SortCol; label: string }) => (
    <th
      className="px-6 py-4 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] cursor-pointer select-none hover:text-gray-700 transition-colors"
      onClick={() => toggleSort(col)}
    >
      <span className="flex items-center gap-0.5">
        {label}
        <SortIcon col={col} active={sort.col} dir={sort.dir} />
      </span>
    </th>
  );

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500 pb-20">

      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="space-y-4">
          <Link
            to="/exams/examiner"
            className="flex items-center gap-2 text-gray-400 hover:text-gray-900 transition-colors text-[10px] font-black uppercase tracking-widest"
          >
            <ArrowLeft className="w-3 h-3" /> Back to Dashboard
          </Link>
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-[2rem] bg-gray-900 flex items-center justify-center text-white shadow-2xl">
              <History className="w-8 h-8" />
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl font-black text-gray-900 tracking-tighter uppercase leading-none">
                  {dietLoading ? "Loading…" : (diet?.title ?? "Diet Grading")}
                </h1>
                {diet?.status && (
                  <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${STATUS_COLORS[diet.status] ?? ""}`}>
                    {diet.status}
                  </span>
                )}
              </div>
              <p className="text-gray-400 text-xs mt-2 font-bold uppercase tracking-widest">
                Open: {fmt(diet?.applications_open_at)}
                <span className="mx-2">•</span>
                Close: {fmt(diet?.applications_close_at)}
                <span className="mx-2">•</span>
                {filtered.length} Application{filtered.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start md:self-auto">
          {canPublish && (
            <button
              onClick={doPublishAll}
              disabled={publishingAll || !hasUnpublished}
              className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-emerald-600 text-white hover:bg-emerald-700 transition-all text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
              title={hasUnpublished ? "Publish every result in this diet — closes grading" : "Every result in this diet is already published"}
            >
              <CheckCircle2 className="w-4 h-4" />
              {publishingAll ? "Publishing…" : "Publish all results"}
            </button>
          )}
          <button
            onClick={() => { refetchDiet(); refetchApps(); setLocalApps([]); }}
            className="p-3.5 rounded-2xl border-2 border-gray-100 hover:bg-gray-50 transition-all"
          >
            <RefreshCw className={`w-4 h-4 text-indigo-500 ${isBusy ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* ── Stats ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total",     val: applications.length, icon: Users,        color: "gray"    },
          { label: "Graded",    val: gradedCount,          icon: Pencil,       color: "indigo"  },
          { label: "Pending",   val: pendingCount,         icon: Clock,        color: "amber"   },
          { label: "Published", val: publishedCount,       icon: CheckCircle2, color: "emerald" },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-gray-100 rounded-[2rem] p-6 shadow-sm flex items-center gap-4">
            <div className={`w-10 h-10 rounded-2xl bg-${s.color}-50 flex items-center justify-center shrink-0`}>
              <s.icon className={`w-5 h-5 text-${s.color}-500`} />
            </div>
            <div>
              <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{s.label}</div>
              <div className="text-2xl font-black text-gray-900 tracking-tighter">
                {appsLoading ? <Loader2 className="w-4 h-4 animate-spin text-gray-300" /> : s.val}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Applications Table ─────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-50 bg-gray-50/50 flex flex-col sm:flex-row gap-4 items-center">
          <div className="flex items-center gap-3 mr-auto">
            <Users className="w-4 h-4 text-indigo-600" />
            <h2 className="text-[10px] font-black uppercase tracking-widest text-gray-900">
              Applications ({filtered.length})
            </h2>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Name, email or exam code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-bold focus:ring-4 ring-indigo-50 transition-all outline-none"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X className="w-3.5 h-3.5 text-gray-400 hover:text-gray-700" />
              </button>
            )}
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full sm:w-40 py-2 px-3 bg-white border border-gray-200 rounded-xl text-[10px] font-black uppercase tracking-widest outline-none"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="paid">Paid</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/80 border-b border-gray-50 text-left">
                <Th col="name"         label="Applicant" />
                <Th col="center"       label="Center" />
                <Th col="status"       label="Status" />
                <Th col="result"       label="Result" />
                <Th col="submitted_at" label="Submitted" />
                <th className="px-6 py-4 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {appsLoading && (
                <tr>
                  <td colSpan={6} className="py-20 text-center">
                    <Loader2 className="w-8 h-8 mx-auto animate-spin text-gray-200" />
                  </td>
                </tr>
              )}
              {!appsLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-20 text-center">
                    <Users className="w-10 h-10 mx-auto text-gray-100 mb-3" />
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">No applications found</p>
                  </td>
                </tr>
              )}
              {filtered.map((app: any) => {
                const result    = app.result;
                const isGraded  = !!result;
                const papers: any[] = app.papers ?? [];

                const isPublished = !!result?.published_at;
                const status      = result?.overall_status;
                const isPass      = ["passed", "recommended"].includes(status);
                const isFail      = ["failed", "not_recommended", "full_reset"].includes(status);
                const isPartial   = status === "partial_pass";
                const isAbsent    = status === "absent";

                // Row tint: pass / fail / partial / absent get separate colors;
                // published rows use a deeper shade plus a left accent.
                const rowBg = !result
                  ? "hover:bg-gray-50/80"
                  : isPass
                  ? (isPublished ? "bg-emerald-100/70 hover:bg-emerald-100" : "bg-emerald-50/50 hover:bg-emerald-50")
                  : isFail
                  ? (isPublished ? "bg-rose-100/70 hover:bg-rose-100" : "bg-rose-50/50 hover:bg-rose-50")
                  : isPartial
                  ? (isPublished ? "bg-amber-100/70 hover:bg-amber-100" : "bg-amber-50/50 hover:bg-amber-50")
                  : isAbsent
                  ? (isPublished ? "bg-gray-200/70 hover:bg-gray-200" : "bg-gray-50 hover:bg-gray-100")
                  : "hover:bg-gray-50/80";

                return (
                  <tr
                    key={app.id}
                    className={`group transition-all ${rowBg} ${
                      isPublished ? "border-l-4 border-blue-500" : ""
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div className="font-black text-gray-900 text-sm">{app.user?.name ?? "—"}</div>
                      <div className="text-[10px] font-bold text-gray-400 mt-0.5">{app.user?.email}</div>
                      {papers.length > 0 && (
                        <div className="text-[9px] font-black text-indigo-400 uppercase tracking-widest mt-0.5">
                          {papers.length} paper{papers.length !== 1 ? "s" : ""}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs font-bold text-gray-600 uppercase tracking-tight">{app.center?.name ?? "—"}</div>
                      {app.center?.state && (
                        <div className="text-[10px] font-bold text-gray-400 mt-0.5 uppercase">{app.center.state}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${APP_STATUS_COLORS[app.status] ?? ""}`}>
                        {app.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {result ? (
                        <div className="space-y-1">
                          <span className={`inline-block px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${RESULT_STATUS_COLORS[result.overall_status] ?? ""}`}>
                            {result.overall_status?.replace(/_/g, " ")}
                          </span>
                          {result.remarks && (
                            <div className="flex items-center gap-1.5 mt-1">
                              <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Remark:</span>
                              <span className="w-6 h-6 rounded-lg bg-gray-900 text-white text-[10px] font-black flex items-center justify-center uppercase">
                                {result.remarks}
                              </span>
                            </div>
                          )}
                          {result.papers_sat != null && (
                            <div className="text-[9px] font-bold text-gray-400 tracking-tighter">
                              {result.papers_passed}/{result.papers_sat} papers passed
                            </div>
                          )}
                          <div className="pt-1">
{result.published_at ? (
                              <span
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest text-emerald-600 bg-emerald-50 border border-emerald-100"
                                title={`Released to the candidate ${fmt(result.published_at)}`}
                              >
                                <CheckCircle2 className="w-3 h-3" /> Released
                              </span>
                            ) : result.finalized_at ? (
                              <span
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest text-indigo-600 bg-indigo-50 border border-indigo-100"
                                title="Published by the board — the secretariat releases it to the candidate"
                              >
                                <Clock className="w-3 h-3" /> Awaiting release
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest text-amber-600 bg-amber-50 border border-amber-100"
                                title="Awaiting Chief Examiner publication"
                              >
                                <Clock className="w-3 h-3" /> Awaiting Publish
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-[9px] font-black text-gray-300 uppercase tracking-widest">Not graded</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tighter">
                        {app.submitted_at ? fmt(app.submitted_at) : "—"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setGradeTarget(app)}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-[9px] font-black uppercase tracking-widest text-gray-500 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-all"
                        >
                          <Pencil className="w-3 h-3" />
                          {isGraded ? "Update" : "Grade"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!appsLoading && filtered.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-50 bg-gray-50/30 flex items-center justify-between">
            <span className="text-[9px] font-black text-gray-300 uppercase tracking-widest">
              Showing {filtered.length} of {applications.length} application{applications.length !== 1 ? "s" : ""}
            </span>
            {search && (
              <button onClick={() => setSearch("")} className="text-[9px] font-black text-indigo-500 hover:text-indigo-700 uppercase tracking-widest">
                Clear filter
              </button>
            )}
          </div>
        )}
      </div>

      {/* Grade Modal */}
      {gradeTarget && (
        <GradeModal
          app={gradeTarget}
          dietId={dietId}
          onClose={() => setGradeTarget(null)}
          onDone={handleGradeDone}
        />
      )}
    </div>
  );
}
